import PropTypes from "prop-types";
import React, { useEffect } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Tabs, Tab } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { fetchCourseVersionData } from "../../../../redux/actions/contentCoursesActions";
import {
  buildCourseVersionDetailsRoute,
  buildCourseVersionHomeworkRoute,
  buildCourseDetailsVersionsRoute,
  buildCourseDetailsSessionsRoute,
  courseVersionDetails,
  courseVersionHomework,
  courseVersion
} from "../../../../routes/AdminRoutes";
import { Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CourseVersionDetails from "./CourseVersionDetails";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import Homework from "../Homework/Homework";

const selector = makeGetEntities();

const getCourseVersionTabs = (courseId, versionId, isPendingFirstRelease) => {
  const tabs = [
    {
      route: buildCourseVersionDetailsRoute(courseId, versionId),
      title: "manageCourses.courseVersionsDetailsTab"
    }
  ];
  if (!isPendingFirstRelease) {
    tabs.push({
      route: buildCourseVersionHomeworkRoute(courseId, versionId),
      title: "manageCourses.courseVersionHomeworkTab"
    });
  }
  return tabs;
};

const CourseVersionTitle = ({ courseName, releaseVersion }) => {
  return (
    <Breadcrumb
      routes={["Courses", courseName, `Manage Version ${releaseVersion}`]}
    />
  );
};

const CourseVersion = ({
  location: { pathname },
  match: {
    params: { courseId, versionId }
  }
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = React.useState(0);

  const { versionData } = useSelector(({ contentCoursesState }) => ({
    versionData: selector(contentCoursesState, {
      type: "version",
      id: versionId
    }),
    loading: contentCoursesState.loading
  }));

  const isPendingFirstRelease = versionData
    ? versionData.pending && versionData.release === "1.0"
    : false;
  const releaseVersionName = () => {
    if (!versionData) return "";

    if (versionData.pending) {
      return t("courseVersion.details.pendingRelease", {
        releaseVersion: versionData.release
      });
    } else {
      return versionData.release;
    }
  };

  useEffect(() => {
    dispatch(fetchCourseVersionData(courseId, versionId));
  }, []);

  function handleTabChange(newTab) {
    dispatch(push(getCourseVersionTabs(courseId, versionId)[newTab].route));
    setCurrentTab(newTab);
  }

  const handleGoBackToCourseVersionsView = () => {
    // TODO: Remove link to sessions tab after edit sessions form will be implemented
    if (versionData.type === "ILT")
      dispatch(push(buildCourseDetailsSessionsRoute(courseId)));
    else dispatch(push(buildCourseDetailsVersionsRoute(courseId)));
  };

  useEffect(() => {
    const currentPathIndex = getCourseVersionTabs(
      courseId,
      versionId,
      isPendingFirstRelease
    ).findIndex(tab => tab.route === pathname);
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
  }, [pathname]);

  return (
    <AdminPageLayout
      title={
        <CourseVersionTitle
          courseName={versionData ? versionData.name : ""}
          releaseVersion={releaseVersionName()}
        />
      }
      onGoBackClick={handleGoBackToCourseVersionsView}
    >
      {versionData ? (
        <>
          <Tabs
            value={currentTab}
            onChange={(e, newTab) => handleTabChange(newTab)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons={false}
          >
            {getCourseVersionTabs(
              courseId,
              versionId,
              isPendingFirstRelease
            ).map((tab, index) => (
              <Tab key={index} label={t(tab.title)} />
            ))}
          </Tabs>
          <Route
            exact
            path={courseVersion}
            render={() => (
              <Redirect
                to={buildCourseVersionDetailsRoute(courseId, versionId)}
              />
            )}
          />
          <Route
            exact
            path={courseVersionDetails}
            render={props => (
              <CourseVersionDetails {...props} versionData={versionData} />
            )}
          />
          <Route
            exact
            path={courseVersionHomework}
            render={props => (
              <Homework
                {...props}
                training_object_id={versionId}
                courseType="online"
              />
            )}
          />
        </>
      ) : (
        <LoadingSpinner />
      )}
    </AdminPageLayout>
  );
};

CourseVersion.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default CourseVersion;
